/*
 * breadcrumb-trail.js
 * --------------------------------------------------------------------------
 * Rewrites the VISIBLE breadcrumb so it reflects the path the user actually
 * navigated, not collection membership / a parent_collection metafield.
 *
 * The trail is kept per-tab in sessionStorage. Each page emits its identity
 * via the #bc-current-page JSON blob (rendered in layout/theme.liquid on
 * EVERY template, including index/404, so resets and captures are reliable).
 *
 * The trail only DEEPENS on a genuine descent. Descent is decided from two
 * structured signals, never from URL string-guessing:
 *   1. Click origin — a capture-phase listener records whether the outgoing
 *      link lived inside #MainContent (page body = descent) or in the
 *      header / menu / footer / breadcrumb chrome (top-level navigation).
 *   2. Type hierarchy — isValidChild(prevType, curType): a product is a child
 *      of any listing; a collection is a child only of a `page` (landing pages
 *      such as Matching Sets that list collections); an article is a child of
 *      a `blog`. Everything else is top-level.
 * A page appends only when BOTH hold; otherwise the trail resets to the new
 * top-level destination. Revisiting a crumb already in the trail truncates to
 * it (highest precedence). Products are therefore always leaves.
 *
 * This ONLY touches the visible <nav class="breadcrumb" data-breadcrumb-trail>.
 * The BreadcrumbList JSON-LD is left exactly as the server rendered it
 * (canonical, membership-based) — visible (navigation) and structured
 * (canonical) are decoupled.
 *
 * Fallback: if no real descent was captured (direct entry, new tab, arriving
 * via Home or a top-level jump), the trail has < 2 entries and we leave the
 * server-rendered canonical breadcrumb untouched. No-JS users always get it.
 * --------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var KEY = 'etchd_bc_trail';
  var INTENT_KEY = 'etchd_bc_intent';
  var MAX = 6;
  // Account / auth / gift-card surfaces are transparent to the trail: they are
  // not navigational ancestors, so they neither reset nor extend the path.
  var SKIP_PREFIXES = ['customers/', 'password', 'gift_card', 'activate'];

  function readCurrent() {
    var el = document.getElementById('bc-current-page');
    if (!el) return null;
    try {
      var d = JSON.parse(el.textContent);
      if (!d || !d.url) return null;
      return d;
    } catch (e) {
      return null;
    }
  }

  function isSkipped(type) {
    if (!type) return false;
    for (var i = 0; i < SKIP_PREFIXES.length; i++) {
      if (type.indexOf(SKIP_PREFIXES[i]) === 0) return true;
    }
    return false;
  }

  // Is `curType` a child that can sit *under* `prevType` in a breadcrumb?
  // Products are leaves of any listing; collections only nest under landing
  // pages; articles only under their blog. Pages / blogs / search / cart / 404
  // are always top-level (return false).
  function isValidChild(prevType, curType) {
    if (!prevType || !curType) return false;
    if (curType === 'product') return prevType !== 'product';
    if (curType === 'collection') return prevType === 'page';
    if (curType === 'article') return prevType === 'blog';
    return false;
  }

  // Normalise any URL to a comparable path key (drop origin, query, hash,
  // trailing slash) so the same page reached via different routes dedupes.
  function pathKey(url) {
    try {
      return new URL(url, window.location.origin).pathname.replace(/\/+$/, '') || '/';
    } catch (e) {
      return String(url || '').replace(/[?#].*$/, '').replace(/\/+$/, '') || '/';
    }
  }

  function loadTrail() {
    try {
      var arr = JSON.parse(sessionStorage.getItem(KEY));
      return Array.isArray(arr) ? arr : [];
    } catch (e) {
      return [];
    }
  }

  function saveTrail(trail) {
    try {
      sessionStorage.setItem(KEY, JSON.stringify(trail));
    } catch (e) {
      /* sessionStorage unavailable (private mode quota) — degrade silently */
    }
  }

  // The intent set by the click that led to THIS page load. Consumed once.
  function takeIntent() {
    var raw = null;
    try {
      raw = sessionStorage.getItem(INTENT_KEY);
      sessionStorage.removeItem(INTENT_KEY);
    } catch (e) {
      return null;
    }
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function indexOfKey(trail, key) {
    for (var i = 0; i < trail.length; i++) {
      if (trail[i] && pathKey(trail[i].url) === key) return i;
    }
    return -1;
  }

  function escHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function entryOf(cur) {
    return { title: cur.title, url: cur.url, type: cur.type };
  }

  // Build/restore the trail for the page now being shown, then render it.
  // isRestore === true means a bfcache (back/forward) restore: the DOM is a
  // frozen snapshot and the normal load logic never ran, so we re-render from
  // sessionStorage WITHOUT mutating the trail (no click intent is involved).
  function process(isRestore) {
    var cur = readCurrent();
    if (!cur) return;

    var nav = document.querySelector('nav.breadcrumb[data-breadcrumb-trail]');
    var curKey = pathKey(cur.url);

    // Returning Home resets the navigation path for the next descent.
    if (cur.home || curKey === '/') {
      if (!isRestore) {
        saveTrail([]);
        takeIntent();
      }
      return;
    }

    // Intent is the click that led to a REAL navigation. A bfcache restore had
    // no click, so ignore (and don't consume) any intent on restores.
    var intent = isRestore ? null : takeIntent();
    var trail = loadTrail();

    // Auth/account pages are transparent — never mutate or render.
    if (isSkipped(cur.type)) return;

    // Case-type pill switch (Tough / Tough Magnetic / Clear / Clear Magnetic): a
    // sibling product of the SAME design, so it is a lateral swap, not a descent.
    // The crumb names the case-type collection instead of falling back to the
    // canonical "All Products": Home / Tough Magnetic Cases / <product>.
    if (!isRestore && intent && intent.ctype && cur.type === 'product') {
      var ctTrail = [
        { title: intent.ctype.title, url: intent.ctype.url, type: 'collection' },
        entryOf(cur)
      ];
      saveTrail(ctTrail);
      if (nav) render(nav, ctTrail);
      return;
    }

    var existing = indexOfKey(trail, curKey);
    if (existing !== -1) {
      // Page already in the trail (ancestor-crumb click, back/forward, or
      // re-navigation). Render the prefix up to it but DON'T truncate the
      // stored trail, so forward navigation still resolves the deeper entries.
      if (nav) render(nav, trail.slice(0, existing + 1));
      return;
    }

    if (isRestore) {
      // bfcache restore to a page not in the trail (an abandoned branch):
      // leave the frozen breadcrumb as painted rather than corrupt the trail.
      return;
    }

    // New page on a real load: descend — branching off the page we came FROM
    // (intent.fromKey), not the trail tip, which can be stale after a bfcache
    // back-restore left it pointing at a deeper page — or reset.
    var fromIdx =
      intent && intent.mode === 'descend' && intent.fromKey
        ? indexOfKey(trail, intent.fromKey)
        : -1;

    var newTrail;
    if (fromIdx !== -1 && isValidChild(trail[fromIdx].type, cur.type)) {
      newTrail = trail.slice(0, fromIdx + 1);
      newTrail.push(entryOf(cur));
    } else {
      // Lateral / top-level jump, unknown origin, or invalid child → reset.
      newTrail = [entryOf(cur)];
    }

    if (newTrail.length > MAX) newTrail = newTrail.slice(newTrail.length - MAX);
    saveTrail(newTrail);
    if (nav) render(nav, newTrail);
  }

  function render(nav, trail) {
    // Not enough captured path — keep the server-rendered canonical crumb.
    if (trail.length < 2) return;

    var homeLink = nav.querySelector('a');
    if (!homeLink) return;
    var delim = nav.querySelector('.breadcrumb__delimiter');

    // Reuse the template's own Home link + delimiter markup so the rewrite is
    // visually identical to whatever Liquid rendered for this template.
    var homeHTML = homeLink.outerHTML;
    var delimHTML = delim ? delim.outerHTML : '<div class="breadcrumb__delimiter"></div>';

    var html = homeHTML;
    for (var i = 0; i < trail.length; i++) {
      var e = trail[i];
      html += delimHTML;
      if (i < trail.length - 1) {
        html +=
          '<a href="' + escHtml(e.url) + '" class="focus-inset">' + escHtml(e.title) + '</a>';
      } else {
        html += '<span>' + escHtml(e.title) + '</span>';
      }
    }
    nav.innerHTML = html;
  }

  // Record, before the browser navigates, whether the clicked link is a descent
  // (lives in the page body) or a top-level jump (header / menu / footer /
  // breadcrumb chrome). Stored against the current page key so a stale intent
  // can't be mis-applied to an unrelated later navigation.
  function onClick(e) {
    if (e.defaultPrevented) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!a) return;
    if (a.target === '_blank' || a.hasAttribute('download')) return;

    var href = a.getAttribute('href') || '';
    if (!href || href.charAt(0) === '#' || /^(mailto:|tel:|javascript:)/i.test(href)) return;

    var dest;
    try {
      dest = new URL(a.href, window.location.origin);
    } catch (err) {
      return;
    }
    if (dest.origin !== window.location.origin) return;

    // Descent = inside the main content body, but not the breadcrumb itself.
    var descend = !a.closest('.breadcrumb') && !!a.closest('#MainContent');

    // Case-type pills (snippets/case-type-selector.liquid) swap to a sibling product.
    // The pill label is the type name ("Tough Magnetic"); its collection is
    // "<label> Cases" at /collections/<handle>-cases (tough-magnetic-cases, ...).
    var ctype = null;
    var pill = a.closest('[data-ct-pill]');
    if (pill) {
      var label = (pill.textContent || '').replace(/\s+/g, ' ').trim();
      if (label) {
        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        ctype = {
          title: label + ' Cases',
          url: root + 'collections/' + label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') + '-cases'
        };
      }
    }

    try {
      sessionStorage.setItem(
        INTENT_KEY,
        JSON.stringify({
          mode: descend ? 'descend' : 'top',
          fromKey: pathKey(window.location.href),
          ctype: ctype
        })
      );
    } catch (err) {
      /* ignore */
    }
  }

  function init() {
    // Capture phase so the intent is recorded even if a handler stops
    // propagation before the default navigation.
    document.addEventListener('click', onClick, true);

    // Back/forward navigation often restores the page from the bfcache without
    // firing DOMContentLoaded, leaving the breadcrumb frozen/stale (and the
    // trail tip pointing at a deeper page). Re-render from sessionStorage on
    // bfcache restores, detected via event.persisted === true.
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) process(true);
    });

    try {
      process(false);
    } finally {
      // Reveal the crumb (hidden by the head script while a case-type rewrite is pending).
      document.documentElement.classList.remove('bc-pending');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
